import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DonationFormBaseValues } from '@/app/components/DonationForm/types';
import { useCurrencyTranslation } from '@/app/hooks/useCurrencyTranslation';
import { getCountryConfig } from '@/app/utils/countryConfig';
import { formatPrice } from '@/app/utils/formatting';

import FormInput from '@/app/components/Input/FormInput';
import FormTipForDonioInput from '@/app/components/TipForDonioInput/FormTipForDonioInput';
import { TipForDonioInputLevel } from '@/app/components/TipForDonioInput/TipForDonioInput';

import AmountBasedFormTipForDonioInput from '@/app/components/DonationForm/donationFormParts/AmountBasedFormTipForDonioInput/AmountBasedFormTipForDonioInput';
import DonationFormSection, {
  TitleMarginBottomSize,
} from '@/app/components/DonationForm/donationFormParts/DonationFormSection';
import { getMaxAmountBasedTipOption } from '@/app/utils/countryConfig/amountBasedTipOptions';

type TipFormSectionProps = {
  amount: number;
  sectionTitle?: string;
  onTipPercentageChange?: (nextTipPercentage: number) => void;
  allowAAmountBasedTip?: boolean;
};

const countryConfig = getCountryConfig();

export const TipFormSection = ({
  sectionTitle,
  amount,
  onTipPercentageChange,
  allowAAmountBasedTip = true,
}: TipFormSectionProps) => {
  const { t } = useTranslation();
  const currencyTranslation = useCurrencyTranslation();
  const { values, setFieldValue } = useFormikContext<DonationFormBaseValues>();

  const displayedValueOverride = useMemo(() => {
    if (typeof values.customTipAmount === 'undefined' || !values.customTipAmount) {
      return undefined;
    }

    const parsedValue = Number(values.customTipAmount);

    if (Number.isNaN(parsedValue)) {
      return undefined;
    }

    return formatPrice(parsedValue);
  }, [values.customTipAmount, formatPrice]);

  return (
    <DonationFormSection
      order={4}
      title={sectionTitle}
      titleMarginBottomSize={TitleMarginBottomSize.Large}
      testId="tip-form-section"
    >
      <h2 className="font-medium mb-[24px]">{t('components.DonationForm.weExistBecauseOfYourHelp')}</h2>
      <p className="mb-[16px]">{t('components.DonationForm.noFeesOrCommission')}</p>
      {amount >= countryConfig.donationFormCommon.donationAmountMinimum &&
      allowAAmountBasedTip &&
      amount <= getMaxAmountBasedTipOption() ? (
        <AmountBasedFormTipForDonioInput
          amount={amount}
          name="tipAmount"
          label={t('components.DonationForm.donateAdditional')}
          className="mb-[48px]"
          onChange={() => setFieldValue('customTipAmount', undefined)}
          displayedValueOverride={displayedValueOverride}
          inputForZeroTipPercentage={
            <FormInput
              type="number"
              name="customTipAmount"
              placeholder={t('components.TipFormSection.customTipAmountPlaceholder')}
              after={currencyTranslation}
              className="!mb-[0]"
            />
          }
        />
      ) : (
        <FormTipForDonioInput
          name="tipPercentage"
          label={t('components.DonationForm.donateAdditional')}
          className="mb-[48px]"
          onChange={onTipPercentageChange}
          displayedValueOverride={displayedValueOverride}
          level={
            !!amount && amount > countryConfig.donationFormCommon.tipBreakpointForDefaultValue
              ? TipForDonioInputLevel.HIGH_AMOUNT_GIVEN
              : TipForDonioInputLevel.LOW_AMOUNT_GIVEN
          }
          inputForZeroTipPercentage={
            <FormInput
              type="number"
              name="customTipAmount"
              placeholder={t('components.TipFormSection.customTipAmountPlaceholder')}
              after={currencyTranslation}
              className="!mb-[0]"
            />
          }
        />
      )}
    </DonationFormSection>
  );
};
